/**
 * A wrapper that coordinates a group of `as-accordion` elements.
 *
 * ## Attributes
 * - `exclusive-open`: When present, only one child accordion may be open at a time.
 *   Opening one will automatically close the others.
 *
 * ## Usage
 * ```html
 * <as-accordion-wrapper exclusive-open>
 *   <as-accordion header-default-text="Section A">...</as-accordion>
 *   <as-accordion header-default-text="Section B">...</as-accordion>
 *   <as-accordion header-default-text="Section C">...</as-accordion>
 * </as-accordion-wrapper>
 * ```
 */
import './as-accordion.ts';
import type { AsAccordion } from './as-accordion.ts';

export class AsAccordionWrapper extends HTMLElement {
  static readonly tag = 'as-accordion-wrapper' as const;

  constructor() {
    super();
    this.style.display = 'block';
  }

  connectedCallback(): void {
    this.addEventListener('toggle', this._handleToggle);
  }

  disconnectedCallback(): void {
    this.removeEventListener('toggle', this._handleToggle);
  }

  private _handleToggle = (e: Event): void => {
    if (!this.hasAttribute('exclusive-open')) return;

    const event = e as CustomEvent<{ open: boolean }>;
    if (!event.detail.open) return;

    const toggled = e.target as HTMLElement;
    // toggle bubbles, so nested accordions and collapse-content reach here too.
    if (toggled.parentElement !== this || toggled.tagName !== 'AS-ACCORDION') return;
    const accordions = Array.from(this.querySelectorAll<AsAccordion>(':scope > as-accordion'));
    for (const accordion of accordions) {
      if (accordion !== toggled && accordion.open) {
        accordion.open = false;
      }
    }
  };
}

if (!customElements.get(AsAccordionWrapper.tag)) {
  customElements.define(AsAccordionWrapper.tag, AsAccordionWrapper);
} else {
  console.warn('as-accordion-wrapper already defined');
}
